import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import {
    useItemTranslations,
    INTERFACE_STATE,
} from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';
import { getAttributeInfo } from '../helpers/getAttributeInfo.ts';

import { type PdpRespTabsCustomizationPanel_user$key } from './__generated__/PdpRespTabsCustomizationPanel_user.graphql.ts';
import { type PdpRespTabsCustomizationPanel_item$key } from './__generated__/PdpRespTabsCustomizationPanel_item.graphql.ts';

import styles from './PdpRespTabsCustomizationPanel.scss';

type Props = {
    user: PdpRespTabsCustomizationPanel_user$key | null;
    item: PdpRespTabsCustomizationPanel_item$key;
};

const userFragment = graphql`
    fragment PdpRespTabsCustomizationPanel_user on User {
        ...PdpSharedContactDealerButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespTabsCustomizationPanel_item on Item {
        isDyoCustomizable
        attributes {
            ...getAttributeInfo_itemAttributes
        }
        ...PdpSharedContactDealerButton_item
    }
`;

export const PdpRespTabsCustomizationPanel: FC<Props> = ({ user: userRef, item: itemRef }) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const [state] = useItemTranslations();
    const itemAttributes =
        state?.interfaceState === INTERFACE_STATE.SHOW_ORIGINAL
            ? state?.data?.viewer?.item?.attributes
            : item?.attributes;
    const { isDyoCustomizable, attributes } = item;
    const { customizationOptions = null, isResizable = null } = getAttributeInfo(
        itemAttributes || attributes
    );

    const resizableContent = isResizable ? (
        <div>
            <FormattedMessage
                id="PdpRespTabsCustomizationPanel.resizingAvailable"
                defaultMessage="Resizing is available."
            />
        </div>
    ) : null;

    const dyoCopy = isDyoCustomizable ? (
        <FormattedMessage
            id="PdpRespTabsCustomizationPanel.designYouOwn"
            defaultMessage="This gemstone can be customized into an extraordinary work of art under your direction. Collaborate with the jeweler to create a one-of-a-kind piece. Simply complete the form provided, and the seller will contact you."
        />
    ) : null;

    const launchOrigin = isDyoCustomizable
        ? 'dyo secondary cta'
        : 'contact link in pdp - description';

    return (
        <GridRow>
            <GridCol desktop={4}>
                <div className={styles.content}>
                    {resizableContent}
                    {customizationOptions}
                    {dyoCopy}
                </div>
                <PdpSharedContactDealerButton
                    size="small"
                    buttonType="transparent"
                    launchOrigin={launchOrigin}
                    type={isDyoCustomizable ? 'REQUEST_CUSTOMIZATION_ALT' : 'REQUEST_CUSTOMIZATION'}
                    item={item}
                    user={user}
                    wrapperClassName={styles.contactButton}
                />
            </GridCol>
        </GridRow>
    );
};
